// Core
import {
  Component,
  Input, OnDestroy,
  OnInit
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { finalize, Subject, takeUntil } from 'rxjs';

// Configs

// Models

// Pipes
import { SettingsService } from '@app/core/settings/services/settings.service';
import { IGetApiResponse } from 'v2/modules/shared';
import { StringTransformationType } from 'v2/modules/shared/pipes/string-transformation.pipe';

// Services

// Utilities
import {
  trackByControlValue,
  trackById,
  trackByValue,
} from 'v2/modules/shared/util/track-by.util';

import {
  COST_ALLOCATION_TYPES,
  SettingsKey,
  CostAllocationTypeConstants,
} from './../../../../../app/core/_constant/settings.constants';
import { ProductCategory } from './../../../../../app/core/client/store/client.model';
import { ProductCategoryService } from './../../../../modules/shared/services/api/product-category.service';
import { IIdName } from './../../../shared/interfaces/common.interface';
import { IPurchaseOrderShipmentShipmentExtraCost } from './../../interfaces/purchase-order.interface';

@Component({
  selector: 'lum-purchase-order-shipment-extra-cost-table',
  templateUrl: './purchase-order-shipment-extra-cost-table.component.html',
  styleUrls: ['./purchase-order-shipment-extra-cost-table.component.scss'],
})
export class PurchaseOrderShipmentExtraCostTableComponent
  implements OnInit, OnDestroy
{
  @Input()
  extraCosts :IPurchaseOrderShipmentShipmentExtraCost[] = [];

  @Input()
  parentForm :UntypedFormGroup;

  @Input()
  mode :'create' | 'update' | 'view' = 'view';

  @Input()
  itemProductCategories :IIdName[] = [];

  COST_ALLOCATION_TYPES = COST_ALLOCATION_TYPES;
  defaultCostAllocationType :string =
    CostAllocationTypeConstants.VALUE_BASED_ALLOCATION;
  productCategories :ProductCategory[] = [];
  loadingProductCategories :boolean = false;
  stringTransformationType = StringTransformationType;

  #onDestroy$ = new Subject();

  trackByControlValue = trackByControlValue;
  trackByValue = trackByValue;
  trackById = trackById;

  constructor(
    private fb :UntypedFormBuilder,
    private settingsService :SettingsService,
    private productCategoryService :ProductCategoryService,
  ) {}

  ngOnInit() :void {
    this.#getCostAllocationType();
    this.#getProductCategories();
  }

  ngOnDestroy() :void {
    this.#onDestroy$.next(null);
    this.#onDestroy$.complete();
  }

  onAddExtraCostItem(count :number) :void {
    for (let i = 0; i < count; i++) {
      this.extraCostsFormArray.push(
        this.fb.group({
          name: [''],
          costAllocationType: [
            this.defaultCostAllocationType || '',
            [Validators.required],
          ],
          categories: this.fb.array([]),
          quantity: [0],
          unitPrice: [0],
        }),
      );
    }
  }

  onRemoveExtraCostItem(index :number) :void {
    this.extraCostsFormArray.removeAt(index);
  }

  displayCategory(categoryId :number) :string {
    const category = this.productCategories.find(
      (category) => category.id === categoryId,
    );

    return category ? category.product_category : '';
  }

  #getCostAllocationType() :void {
    this.settingsService
      .getKeyValue(SettingsKey.COST_ALLOCATION_TYPE)
      .pipe(takeUntil(this.#onDestroy$))
      .subscribe({
        next: (costAllocationType :string) => {
          if (costAllocationType) {
            this.defaultCostAllocationType = costAllocationType;
          }
        },
      });
  }

  #getProductCategories(searchQuery :string = '') :void {
    this.loadingProductCategories = true;
    this.productCategoryService
      .get({
        limit: '10',
        page: '1',
        searchQuery,
      })
      .pipe(
        takeUntil(this.#onDestroy$),
        finalize(() => (this.loadingProductCategories = false)),
      )
      .subscribe({
        next: (response :IGetApiResponse) => {
          this.productCategories = response.data;
        },
      });
  }


  get extraCostsFormArray() :UntypedFormArray {
    return this.parentForm.get('shipmentExtraCosts') as UntypedFormArray;
  }

  get totalExtraCostQuantity() :number {
    return this.extraCostsFormArray.controls.reduce(
      (acc, control :UntypedFormGroup) =>
        acc + Number(control.get('quantity').value),
      0,
    );
  }

  get totalExtraCostUnitPrice() :number {
    return this.extraCostsFormArray.controls.reduce(
      (acc, control :UntypedFormGroup) =>
        acc + Number(control.get('unitPrice').value),
      0,
    );
  }

  get totalExtraCostLineTotal() :number {
    return this.extraCostsFormArray.controls.reduce(
      (acc, control :UntypedFormGroup) =>
        acc +
        Number(control.get('quantity').value) * control.get('unitPrice').value,
      0,
    );
  }
}
